import type { ComparisonOffer, ComparisonOfferInput, ComparisonRow } from './ComparisonTypes'

/**
 * ComparisonCalculator — best price/stock/delivery/margin + economy.
 */
export class ComparisonCalculator {
  enrichOffers(offers: ComparisonOfferInput[]): ComparisonOffer[] {
    if (offers.length === 0) return []

    const brl = offers.filter((o) => o.currency === 'BRL')
    const pricePool = brl.length ? brl : offers
    const bestPrice = Math.min(...pricePool.map((o) => o.price))
    const bestStock = Math.max(...offers.map((o) => o.stock))
    const inStockDeliveries = offers.filter((o) => o.stock > 0 || o.channel === 'paraguay')
    const bestDelivery =
      inStockDeliveries.length > 0
        ? Math.min(...inStockDeliveries.map((o) => o.deliveryDays))
        : Math.min(...offers.map((o) => o.deliveryDays))
    const margins = offers.map((o) => o.marginPercent ?? estimateMargin(o))
    const bestMargin = Math.max(...margins)

    return offers.map((offer, index) => {
      const margin = margins[index]!
      const comparable = offer.currency === 'BRL' || pricePool === offers
      const priceDiffFromBest = comparable ? round2(offer.price - bestPrice) : 0
      const priceDiffPercent =
        comparable && bestPrice > 0 ? round2((priceDiffFromBest / bestPrice) * 100) : 0

      return {
        ...offer,
        marginPercent: margin,
        isBestPrice: comparable && offer.price === bestPrice,
        isBestStock: offer.stock === bestStock && offer.stock > 0,
        isBestDelivery: offer.deliveryDays === bestDelivery,
        isBestMargin: margin === bestMargin,
        priceDiffFromBest,
        priceDiffPercent,
      }
    })
  }

  buildRow(fingerprint: string, offers: ComparisonOfferInput[]): ComparisonRow {
    const enriched = this.enrichOffers(offers)
    const product = enriched[0]!.product
    const brl = enriched.filter((o) => o.currency === 'BRL')
    const pricePool = brl.length ? brl : enriched
    const bestPrice = pricePool.length ? Math.min(...pricePool.map((o) => o.price)) : null
    const worstPrice = pricePool.length ? Math.max(...pricePool.map((o) => o.price)) : null
    const economy =
      bestPrice != null && worstPrice != null ? round2(worstPrice - bestPrice) : 0
    const economyPercent =
      bestPrice != null && worstPrice != null && worstPrice > 0
        ? round2((economy / worstPrice) * 100)
        : 0
    const bestStock = Math.max(0, ...enriched.map((o) => o.stock))
    const bestDelivery = enriched.length
      ? Math.min(...enriched.map((o) => o.deliveryDays))
      : null
    const bestMargin = enriched.length
      ? Math.max(...enriched.map((o) => o.marginPercent ?? 0))
      : null
    const recommended =
      enriched.find((o) => o.isBestPrice)?.supplierName ??
      enriched.sort((a, b) => a.price - b.price)[0]?.supplierName ??
      null

    return {
      fingerprint,
      product,
      label: buildLabel(product),
      offers: enriched.sort((a, b) => a.price - b.price),
      bestPrice,
      worstPrice,
      economy,
      economyPercent,
      bestStock,
      bestDelivery,
      bestMargin,
      recommendedSupplier: recommended,
      supplierCount: enriched.length,
    }
  }
}

function buildLabel(product: ComparisonOfferInput['product']): string {
  return [product.brand, product.line, product.model, product.capacity]
    .filter(Boolean)
    .join(' ')
    .trim() || product.fingerprint
}

function estimateMargin(offer: ComparisonOfferInput): number {
  if (offer.marginPercent != null) return offer.marginPercent
  if (offer.purchasePrice != null && offer.price > 0) {
    return round2(((offer.price - offer.purchasePrice) / offer.price) * 100)
  }
  // Assume ~18% retail markup baseline for ranking when purchase unknown
  return offer.channel === 'distributor' ? 22 : offer.channel === 'web' ? 18 : 15
}

function round2(n: number): number {
  return Math.round((n + Number.EPSILON) * 100) / 100
}

export const comparisonCalculator = new ComparisonCalculator()
